import React, { useEffect } from 'react';

import * as Sentry from '@sentry/react';

import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToastContext } from '@resideo/blueprint-react';

import ModalHeader from 'components/common/ModalHeader';
import PageLoading from 'components/common/PageLoading';

import {
  PartnerCloudOAuthRequest,
  RisError,
  StepComponentProps,
  Steps,
} from 'types/types';
import { useRisClient } from 'hooks/useRisClient';

const OAuthSignInStep = ({
  account,
  oAuthData,
  setStep,
  setUserSession,
  closeModal,
}: StepComponentProps) => {
  const { client } = useRisClient();

  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const exchangeOAuthToken = useMutation({
    mutationKey: ['dataStream', 'oAuthTokenExchange'],
    mutationFn: async ({
      code,
      codeVerifier,
      partnerCloudId,
    }: PartnerCloudOAuthRequest) => {
      const { data } = await client.partnerClouds.myUsersObtainTokenByAuthCode(
        partnerCloudId,
        { authCode: code, codeVerifier: codeVerifier }
      );

      if (!data?.accessToken || !data?.externalUserId) {
        Sentry.captureMessage(
          `[Data Streaming] Partner account authentication failed: incomplete response from service provider`,
          'error'
        );

        throw new Error(
          '[Data Streaming] Partner account authentication failed: incomplete response from service provider'
        );
      }

      return {
        accessToken: data.accessToken,
        externalUserId: data.externalUserId,
        encryptedDeviceData: data.encryptedDeviceData || '',
      };
    },

    onSuccess: data => {
      setUserSession(data);
      setStep(Steps.TERMS_ACCEPTANCE);
    },

    onError: (error: RisError) => {
      addToast({
        toastType: 'Error',
        message: t(
          'privacy.dataStream.oauth.error.tokenExchange',
          'Failed to complete OAuth authorization'
        ),
      });

      Sentry.captureEvent({
        message: '[Data Streaming] OAuth token exchange failed',
        level: 'error',
        extra: { partnerCloudId: account.partnerCloudId, error },
      });

      closeModal();
    },

    retry: false,
  });

  useEffect(() => {
    if (
      oAuthData?.code &&
      oAuthData?.codeVerifier &&
      oAuthData?.partnerCloudId
    ) {
      exchangeOAuthToken.mutate(oAuthData);
    } else {
      addToast({
        toastType: 'Error',
        message: t('privacy.dataStream.accountSignIn.oauth.error.generic'),
      });

      closeModal();
    }
  }, [oAuthData]);

  return (
    <>
      <ModalHeader
        title={t('privacy.dataStream.accountSignIn.credentials.title', {
          accountName: account.partnerCloudName,
        })}
        closeModal={closeModal}
      />

      <PageLoading />
    </>
  );
};

export default OAuthSignInStep;
